import { useMemo } from "react";
import { parseDiagram, type Diagram, type DiagramNode } from "@/lib/diagramParser";

interface DiagramCanvasProps {
  text: string;
}

export default function DiagramCanvas({ text }: DiagramCanvasProps) {
  const diagram = useMemo(() => {
    try {
      return parseDiagram(text);
    } catch {
      return { nodes: [], edges: [] } as Diagram;
    }
  }, [text]);

  if (diagram.nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground font-body text-sm">
        Type your flow on the left to see the diagram here
      </div>
    );
  }

  // Calculate viewBox
  const padding = 60;
  const minX = Math.min(...diagram.nodes.map(n => n.x)) - padding;
  const minY = Math.min(...diagram.nodes.map(n => n.y)) - padding;
  const maxX = Math.max(...diagram.nodes.map(n => n.x + n.width)) + padding;
  const maxY = Math.max(...diagram.nodes.map(n => n.y + n.height)) + padding;

  const nodeById = new Map(diagram.nodes.map(n => [n.id, n]));

  return (
    <svg
      viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`}
      className="w-full h-full"
      style={{ minHeight: 300 }}
    >
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="7"
          refX="10"
          refY="3.5"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3.5, 0 7"
            fill="hsl(var(--diagram-connector))"
          />
        </marker>
      </defs>

      {/* Edges */}
      {diagram.edges.map((edge, i) => {
        const from = nodeById.get(edge.from);
        const to = nodeById.get(edge.to);
        if (!from || !to) return null;

        const fromCx = from.x + from.width / 2;
        const fromCy = from.y + from.height / 2;
        const toCx = to.x + to.width / 2;
        const toCy = to.y + to.height / 2;

        // Simple straight line from bottom of source to top of target
        const startY = fromCy < toCy ? from.y + from.height : from.y;
        const endY = fromCy < toCy ? to.y : to.y + to.height;
        const startX = fromCx;
        const endX = toCx;

        // If same row, connect side-to-side
        const sameRow = Math.abs(from.y - to.y) < 10;
        const sx = sameRow ? (fromCx < toCx ? from.x + from.width : from.x) : startX;
        const sy = sameRow ? fromCy : startY;
        const ex = sameRow ? (fromCx < toCx ? to.x : to.x + to.width) : endX;
        const ey = sameRow ? toCy : endY;

        const midY = (sy + ey) / 2;
        const path = sameRow
          ? `M ${sx} ${sy} L ${ex} ${ey}`
          : `M ${sx} ${sy} C ${sx} ${midY} ${ex} ${midY} ${ex} ${ey}`;

        return (
          <g key={i}>
            <path
              d={path}
              fill="none"
              stroke="hsl(var(--diagram-connector))"
              strokeWidth="1.5"
              markerEnd="url(#arrowhead)"
            />
            {edge.label && (
              <text
                x={(sx + ex) / 2 + 8}
                y={(sy + ey) / 2 - 4}
                fill="hsl(var(--diagram-highlight))"
                fontSize="11"
                fontFamily="var(--font-mono)"
                fontWeight="500"
              >
                {edge.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Nodes */}
      {diagram.nodes.map((node) => (
        <NodeShape key={node.id} node={node} />
      ))}
    </svg>
  );
}

function NodeShape({ node }: { node: DiagramNode }) {
  const cx = node.x + node.width / 2;
  const cy = node.y + node.height / 2;

  const shapeEl = (() => {
    switch (node.type) {
      case "start":
        return (
          <rect
            x={node.x}
            y={node.y}
            width={node.width}
            height={node.height}
            rx={node.height / 2}
            fill="hsl(var(--primary))"
            stroke="hsl(var(--primary))"
            strokeWidth="1.5"
          />
        );
      case "decision":
        const hw = node.width / 2;
        const hh = node.height / 2 + 6;
        return (
          <polygon
            points={`${cx},${cy - hh} ${cx + hw},${cy} ${cx},${cy + hh} ${cx - hw},${cy}`}
            fill="hsl(var(--diagram-node))"
            stroke="hsl(var(--diagram-highlight))"
            strokeWidth="2"
          />
        );
      case "io":
        const skew = 12;
        return (
          <polygon
            points={`${node.x + skew},${node.y} ${node.x + node.width},${node.y} ${node.x + node.width - skew},${node.y + node.height} ${node.x},${node.y + node.height}`}
            fill="hsl(var(--diagram-node))"
            stroke="hsl(var(--border))"
            strokeWidth="1.5"
          />
        );
      default:
        return (
          <rect
            x={node.x}
            y={node.y}
            width={node.width}
            height={node.height}
            rx={4}
            fill="hsl(var(--diagram-node))"
            stroke="hsl(var(--border))"
            strokeWidth="1.5"
          />
        );
    }
  })();

  const textColor = node.type === "start"
    ? "hsl(var(--primary-foreground))"
    : "hsl(var(--diagram-node-foreground))";

  return (
    <g>
      {shapeEl}
      <text
        x={cx}
        y={cy + 1}
        textAnchor="middle"
        dominantBaseline="central"
        fill={textColor}
        fontSize="12"
        fontFamily="var(--font-body)"
        fontWeight="500"
      >
        {node.label.length > 20 ? node.label.slice(0, 18) + "…" : node.label}
      </text>
    </g>
  );
}
